import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useEffect, useMemo, useRef, useState } from 'react';

import { GridItem, Separator, useBreakpointValue } from '@chakra-ui/react';

import {
    DataLayerPageTypeEnum,
    imageHelpers,
    urlHelpers,
    WpEventParams,
    WpPageType,
} from '@nocowanie/core';
import { BreadCrumbs, GridWrapper, SeoLinks } from '@nocowanie/common-ui';

import { MetaTags } from '@app/components/atoms/meta-tags';
import { RichSnippets } from '@app/components/atoms/rich-snippets';
import { AccommodationStickyBar } from '@app/components/molecules/accommodation-sticky-bar';
import { AccommodationPricingWrapper } from '@app/components/organisms/accommodation-pricing';
import { Amenities } from '@app/components/organisms/amenities';
import { AmenitiesBar } from '@app/components/organisms/amenities-bar';
import { BasicInformation } from '@app/components/organisms/basic-information';
import { Description } from '@app/components/organisms/description';
import { ImportantInfo } from '@app/components/organisms/important-info';
import { Location } from '@app/components/organisms/location';
import { MainGallery } from '@app/components/organisms/main-gallery';
import { OfferManager } from '@app/components/organisms/offer-manager';
import { Reviews } from '@app/components/organisms/reviews';
import { Support } from '@app/components/organisms/support';
import { DefaultTemplate } from '@app/components/templates/default-template';
import { META_IMAGE_DIMENSIONS } from '@app/consts';
import { environment } from '@app/environments/environment';
import { getIdFromSlug } from '@app/helpers/slug.helpers';
import {
    useAccommodationQueryParams,
    useDataLayerAccommodationEvents,
    usePageDataLayerEvents,
    useWphPixelEvents,
} from '@app/hooks';
import { PageProps } from '@app/models';
import { idServerSidePropsHandler } from '@app/pages/rezerwuj/[id].server-side-props.handler';
import {
    accommodationStaticSlice,
    commonUiStateSlice,
    nocowanieAppStateWrapper,
    useAppSelector,
} from '@app/state';
import { selectors as commonUiStateSelectors } from '@app/state/slices/common-ui-state.slice';

const BestOfferSticky = dynamic(
    () => import('@app/components/organisms/best-offer').then(x => x.BestOfferSticky),
    {
        ssr: false,
    },
);

const createGridItem = (areaName: string, component: JSX.Element, key: number) => {
    return (
        <GridItem key={key} area={areaName}>
            {component}
        </GridItem>
    );
};

const AccommodationPage: NextPage<PageProps> = props => {
    nocowanieAppStateWrapper.useHydration(props);

    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isMobileRes = useBreakpointValue(
        {
            md: false,
            base: true,
        },
        {
            fallback: isMobileBrowser ? 'base' : 'md',
        },
    );

    const withStickyFooter = useBreakpointValue({
        md: false,
        base: true,
    });
    const { t } = useTranslation('accommodation');

    const {
        query: { id },
        asPath: pagePath,
    } = useRouter();

    useAccommodationQueryParams();

    const accommodationId = getIdFromSlug(id);
    const { sendOfferDataLayerEvent, sendSearchResultsDataLayerEvent } =
        useDataLayerAccommodationEvents(accommodationId);
    const accommodationType = useAppSelector(
        accommodationStaticSlice.selectors.selectAccommodationType,
    );
    const isPageDataLayerSent = useAppSelector(commonUiStateSelectors.selectIsPageDataLayerSent);
    const [isOfferDataLayerSent, setIsOfferDataLayerSent] = useState(false);
    usePageDataLayerEvents({
        pageType: DataLayerPageTypeEnum.Offer,
        pagePath,
    });

    const sectionRefs = {
        location: useRef<HTMLDivElement>(null),
        pricing: useRef<HTMLDivElement>(null),
        reviews: useRef<HTMLDivElement>(null),
    };

    const breadcrumbs = useAppSelector(
        accommodationStaticSlice.selectors.selectBreadcrumbsListData,
    );
    const { mainImg } = useAppSelector(accommodationStaticSlice.selectors.selectMetaTagsData);
    const { name } = useAppSelector(accommodationStaticSlice.selectors.selectBasicInformationData);
    const { ratingCount } = useAppSelector(accommodationStaticSlice.selectors.selectReviewsData);
    const seoLinksState = useAppSelector(accommodationStaticSlice.selectors.selectSeoLinksData);

    const seoLinks = useMemo(() => seoLinksState, []);
    const accommodationUrl =
        accommodationId && name
            ? urlHelpers.getAccommodationUrl(accommodationId, name, {}, environment.NOCOWANIE_URL)
            : undefined;
    const { sendViewContentEvent } = useWphPixelEvents(WpPageType.Product);

    const components = {
        breadcrumbs: {
            areaName: 'breadcrumbs',
            component: (
                <BreadCrumbs
                    breadcrumbs={breadcrumbs}
                    name={name}
                    baseUrl={process.env.NEXT_PUBLIC_NOCOWANIE_URL}
                />
            ),
        },
        basicInfo: {
            areaName: 'basic-information',
            component: (
                <BasicInformation
                    scollToLocationRef={sectionRefs.location}
                    scollToReviewsRef={sectionRefs.reviews}
                    mb={{
                        base: 0,
                        md: 2,
                    }}
                />
            ),
        },
        photosSection: {
            areaName: 'photos-section',
            component: <MainGallery ml={-4} mr={-4} />,
        },
        amenitiesBar: {
            areaName: 'amenities-bar',
            component: (
                <AmenitiesBar
                    mx={{
                        base: '-gutterWidth',
                        md: 0,
                    }}
                />
            ),
        },
        description: {
            areaName: 'description',
            component: <Description />,
        },
        bestOffer: {
            // This section is displayed on desktop only
            areaName: 'best-offer',
            component: !isMobileRes ? <BestOfferSticky /> : null,
        },
        offerManager: {
            // This section is displayed only when there are any revies - DNI-1642
            areaName: 'offer-manager',
            component: ratingCount ? <OfferManager /> : null,
        },
        location: {
            areaName: 'location',
            component: <Location ref={sectionRefs.location} />,
        },
        reviews: {
            areaName: 'reviews',
            component: <Reviews ref={sectionRefs.reviews} />,
        },
        pricing: {
            areaName: 'accommodation-configurator',
            component: <AccommodationPricingWrapper ref={sectionRefs.pricing} />,
        },
        pricingWithExpandedMobileView: {
            areaName: 'pricing-mobile',
            component: isMobileRes ? (
                <>
                    <Separator orientation="horizontal" mb={6} />
                    <AccommodationPricingWrapper withModals={false} expandedMobileView />
                </>
            ) : null,
        },
        amenities: {
            areaName: 'amenities',
            component: <Amenities />,
        },
        importantInfo: {
            areaName: 'important-info',
            component: <ImportantInfo />,
        },
        seoLinks: {
            areaName: 'seo-links',
            component: seoLinks?.length ? <SeoLinks linkSections={seoLinks} mb={6} /> : null,
        },
    };

    useEffect(() => {
        // the order is important ('offer' event should be sent after 'pageview' event - after `isPageDataLayerSent` becomes true)
        if (!(!isOfferDataLayerSent && isPageDataLayerSent)) {
            return;
        }

        const wpViewEventParams = {
            ...(accommodationId && { id: accommodationId }),
            ...(accommodationType && { content_category: accommodationType }),
            // content_category_id is not available
        };

        sendViewContentEvent(wpViewEventParams as WpEventParams);
        sendOfferDataLayerEvent();

        // an event triggered after redirection from the search form directly to the accommodation page
        // (one of the scenarios described in DNI-1265)
        sendSearchResultsDataLayerEvent();
        setIsOfferDataLayerSent(true);
    }, [
        isPageDataLayerSent,
        accommodationType,
        accommodationId,
        sendViewContentEvent,
        sendOfferDataLayerEvent,
        sendSearchResultsDataLayerEvent,
        isOfferDataLayerSent,
    ]);

    return (
        <>
            <MetaTags
                title={name}
                description={t('metaTags.descriptionSuffix', {
                    value: name,
                })}
                url={accommodationUrl}
                imgUrl={
                    mainImg ? imageHelpers.wpresizer(META_IMAGE_DIMENSIONS, mainImg) : undefined
                }
                showCanonicalUrl={true}
                showTwitterMetaTags={true}
            />
            <RichSnippets accommodationId={accommodationId} />
            <DefaultTemplate
                stickyElement={
                    withStickyFooter ? (
                        <AccommodationStickyBar
                            pricingSectionRef={sectionRefs.pricing}
                            isVisible={false}
                        />
                    ) : null
                }
                showScrollTopButton={!!props.showScrollTopButton}
            >
                <GridWrapper
                    gridTemplateColumns={{ base: 'minmax(0, 1fr)', md: 'minmax(0, 11fr) 5fr' }}
                    gridTemplateAreas={{
                        base: `
                        "${components.breadcrumbs.areaName}"
                        "${components.photosSection.areaName}"
                        "${components.basicInfo.areaName}"
                        "${components.pricing.areaName}"
                        "${components.amenitiesBar.areaName}"
                        "${components.description.areaName}"
                        "${components.offerManager.areaName}"
                        "${components.location.areaName}"
                        "${components.reviews.areaName}"
                        "${components.amenities.areaName}"
                        "${components.pricingWithExpandedMobileView.areaName}"
                        "${components.importantInfo.areaName}"
                        "${components.seoLinks.areaName}"
                        `,
                        md: `
                        "${components.breadcrumbs.areaName}               ${components.breadcrumbs.areaName}"
                        "${components.basicInfo.areaName}                 ${components.basicInfo.areaName}"
                        "${components.photosSection.areaName}             ${components.bestOffer.areaName}"
                        "${components.amenitiesBar.areaName}              ${components.bestOffer.areaName}"
                        "${components.description.areaName}               ${components.bestOffer.areaName}"
                        "${components.offerManager.areaName}              ${components.bestOffer.areaName}"
                        "${components.pricing.areaName}                   ${components.bestOffer.areaName}"
                        "${components.location.areaName}                  ${components.bestOffer.areaName}"
                        "${components.reviews.areaName}                   ${components.bestOffer.areaName}"
                        "${components.amenities.areaName}                 ${components.bestOffer.areaName}"
                        "${components.importantInfo.areaName}             ${components.bestOffer.areaName}"
                        "${components.seoLinks.areaName}                  ${components.seoLinks.areaName}"
                        `,
                    }}
                >
                    {Object.values(components).map((componentObject, index) =>
                        componentObject.component
                            ? createGridItem(
                                  componentObject.areaName,
                                  componentObject.component,
                                  index,
                              )
                            : undefined,
                    )}
                </GridWrapper>
                {!isMobileRes ? <Support /> : null}
            </DefaultTemplate>
        </>
    );
};

export const getServerSideProps = idServerSidePropsHandler;

export default AccommodationPage;
